/**
 * This code is based on the Tiptap `CharacterCount` extension:
 * https://github.com/ueberdosis/tiptap/blob/main/packages/extensions/src/character-count/character-count.ts
 * It has been heavily modified to fit our needs.
 */
import crel from "crelt";
import HTML from "html-parse-stringify";
import type { Node } from "prosemirror-model";
import { type EditorState, Plugin, PluginKey } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";

import { t } from "i18n/i18n";

interface CharacterCountState {
  limit: number | null;
  display: "NONE" | "READ" | "EDIT";
}

export interface CharacterCountOptions {
  limit: number | null; // max character limit (null = no limit)
  enforce: boolean; // block input beyond the limit
  mode: "textSize" | "nodeSize"; // textSize = text characters only, nodeSize = text + ProseMirror positional tokens
  textCounter: (text: string) => number; // custom counter fn; defaults to text.length
  position: "before" | "after"; // placement of the count UI relative to the editor
  display: "NONE" | "READ" | "EDIT"; // NONE = hidden, READ = read-only, EDIT = shows limit controls
}

const DEFAULT_LIMIT = 600;
const prefix = "ProseMirror-charcount";
export const key = new PluginKey<CharacterCountState>("characterCount");

export function countCharacters(node: Node, options: Partial<CharacterCountOptions> = {}): number {
  if (options.mode === "nodeSize") return node.nodeSize;
  const text = node.textBetween(0, node.content.size, "⏎", " ");
  return options.textCounter ? options.textCounter(text) : text.length;
}

class CharacterCountView {
  wrapper: HTMLDivElement;

  ui: HTMLDivElement;

  checkbox: HTMLInputElement | null = null;

  input: HTMLInputElement | null = null;

  firstRun: boolean = true;

  constructor(
    readonly editorView: EditorView,
    readonly options: Partial<CharacterCountOptions> = {},
  ) {
    this.wrapper = crel("div", {
      class: `${prefix}-wrapper relative grow group flex flex-col`,
    }) as HTMLDivElement;
    this.ui = this.wrapper.appendChild(
      crel("div", {
        class: `${prefix} absolute right-3 bottom-1 z-[1]`,
      }),
    ) as HTMLDivElement;

    if (editorView.dom.parentNode) {
      editorView.dom.parentNode.replaceChild(this.wrapper, editorView.dom);
    }

    const position = options.position || "after";
    if (position === "after") {
      this.wrapper.insertBefore(editorView.dom, this.wrapper.firstChild);
    } else {
      this.wrapper.appendChild(editorView.dom);
    }

    this.update(editorView, editorView.state);
  }

  update(editorView: EditorView, prevState: EditorState) {
    const defaults = {
      limit: this.options?.limit || null,
      display: this.options?.display || "NONE",
    };
    const { limit, display } = key.getState(editorView.state) || defaults;
    const count = countCharacters(editorView.state.doc, this.options);

    const { limit: prevLimit, display: prevDisplay } = key.getState(prevState) || defaults;
    const prevCount = countCharacters(prevState.doc, this.options);

    // Only update the UI if the count, limit, or display mode has changed
    if (!this.firstRun && count === prevCount && limit === prevLimit && display === prevDisplay) {
      return;
    }
    this.firstRun = false;

    const dom = editorView.dom;
    const limitReached = limit !== null && limit > 0 && count >= limit;
    const limitExceeded = limit !== null && limit > 0 && count > limit;

    // If we are *at* the limit, set a data attribute on the wrapper
    if (limitReached) {
      this.wrapper.dataset.charLimitReached = "true";
    } else if (this.wrapper.dataset.charLimitReached) {
      delete this.wrapper.dataset.charLimitReached;
    }
    // If we have *exceeded* the limit, also set aria-invalid to true on the editor itself
    if (limitExceeded) {
      dom.setAttribute("aria-invalid", "true");
    } else if (dom.getAttribute("aria-invalid") === "true") {
      dom.removeAttribute("aria-invalid");
    }

    const checkboxHasFocus = this.checkbox === document.activeElement;
    const inputHasFocus = this.input === document.activeElement;

    // Clear the UI and re-render it
    this.ui.innerHTML = "";

    if (display === "READ" && limit) {
      this.ui.role = "status";
      this.ui.setAttribute("aria-live", "polite");
    } else {
      this.ui.removeAttribute("role");
      this.ui.removeAttribute("aria-live");
    }

    if (display === "READ" && limit) {
      this.ui.appendChild(
        crel(
          "div",
          {
            class:
              "rounded border border-transparent bg-blue-tint-95 p-1 text-xs " +
              "group-data-[char-limit-reached=true]:border-red-tint-20 group-data-[char-limit-reached=true]:bg-red-tint-95 group-data-[char-limit-reached=true]:text-red-shade-20",
            "aria-hidden": "true",
          },
          [
            limitReached ? `${t("character_counter.reached_limit")} ` : undefined,
            t("character_counter.characters_used_count", {
              textLength: count,
              maxLength: limit,
            }),
          ],
        ),
      );
      this.ui.appendChild(
        crel(
          "div",
          {
            class: "sr-only",
          },
          t("character_counter.screen_reader_status", {
            count,
            context: count >= limit ? "limited" : undefined,
            maxLength: limit,
          }),
        ),
      );
    } else if (display === "EDIT") {
      this.checkbox = crel("input", {
        type: "checkbox",
        class: "size-4 rounded ring-offset-2 focus-within:ring-2 focus-within:ring-blue-tint-70",
        checked: Boolean(limit),
        "aria-label": t("character_counter.toggle_limit"),
      }) as HTMLInputElement;
      this.checkbox.addEventListener("change", (event) => {
        const checked = (event.currentTarget as HTMLInputElement).checked;
        const newLimit = checked ? DEFAULT_LIMIT : null;
        editorView.dispatch(editorView.state.tr.setMeta(key, { limit: newLimit }));
      });
      this.checkbox.addEventListener("mousedown", (e) => {
        e.preventDefault();
        // Clicking on the checkbox normally causes it to focus. Because the checkbox is focused,
        // this triggers the `:focus-within` CSS selector on the wrapper, which
        // causes the toolbar to appear. This in turn causes the character count UI to
        // jump upwards, which is a jarring experience.
        //
        // Instead, we will use the `mousedown` event to trigger a `change` event on the checkbox,
        // allowing the `mousedown` event to toggle the checkbox state. This prevents the character
        // count UI from jumping around due to the toolbar appearing/disappearing.
        const target = e.currentTarget as HTMLInputElement;
        target.checked = !target.checked;
        (e.currentTarget as HTMLInputElement).dispatchEvent(new Event("change", { bubbles: true }));
      });

      this.input = crel("input", {
        type: "number",
        name: "character-limit",
        placeholder: DEFAULT_LIMIT.toString(),
        class:
          "w-[60px] rounded border border-black-tint-90 bg-white p-1 text-black-tint-20 disabled:border-black-tint-90 disabled:bg-blue-tint-95 disabled:text-black-tint-55",
        value: limit ?? "",
        disabled: !limit,
        "aria-label": t("character_counter.set_character_limit"),
      }) as HTMLInputElement;
      this.input.addEventListener("change", (event) => {
        const value = (event.currentTarget as HTMLInputElement).value;
        const newLimit = value === "" ? null : parseInt(value, 10);
        editorView.dispatch(editorView.state.tr.setMeta(key, { limit: newLimit }));
      });

      // This is what React-i18next uses for interpolating React components,
      // so we need to parse the translated string and insert the checkbox and input in the correct places.
      const parsedText = HTML.parse(t("character_counter.character_limit", { count: limit ?? 0 }));

      this.ui.appendChild(
        crel(
          "div",
          {
            class:
              "inline-flex items-center gap-2 text-sm text-black-tint-40 p-1 rounded bg-blue-tint-95 border border-blue-tint-70",
          },
          [
            this.checkbox,
            parsedText[0].children[0].content,
            this.input,
            parsedText[2].children[0].content,
          ],
        ),
      );

      if (checkboxHasFocus) {
        this.checkbox.focus();
      } else if (inputHasFocus) {
        this.input.focus();
      }
    }
  }

  destroy() {
    if (this.wrapper.parentNode)
      this.wrapper.parentNode.replaceChild(this.editorView.dom, this.wrapper);
  }
}

/** Creates a plugin that tracks and enforces character count limits. */
export function characterCountPlugin(options: Partial<CharacterCountOptions> = {}) {
  const { limit: initialLimit = null, display: initialDisplay = "NONE", enforce = false } = options;
  return new Plugin<CharacterCountState>({
    key,
    state: {
      init() {
        return { limit: initialLimit, display: initialDisplay };
      },
      apply(tr, value) {
        const meta = tr.getMeta(key) as Partial<CharacterCountState> | undefined;
        if (!meta) return value;
        let next = value;
        if (typeof meta.limit !== "undefined") next = { ...next, limit: meta.limit };
        if (typeof meta.display === "string") next = { ...next, display: meta.display };
        return next;
      },
    },
    props: {
      attributes: {
        class: "grow",
      },
    },
    view(editorView) {
      return new CharacterCountView(editorView, options);
    },
    filterTransaction(transaction, state) {
      if (!enforce) return true;

      const { limit } = key.getState(state) || { limit: initialLimit, display: initialDisplay };
      // Nothing has changed or no limit is defined. Ignore it.
      if (!transaction.docChanged || limit === 0 || limit === null || limit === undefined) {
        return true;
      }

      const oldSize = countCharacters(state.doc, options);
      const newSize = countCharacters(transaction.doc, options);

      // Everything is in the limit. Good.
      if (newSize <= limit) {
        return true;
      }

      // The limit has already been exceeded but will be reduced.
      if (oldSize > limit && newSize > limit && newSize <= oldSize) {
        return true;
      }

      // The limit has already been exceeded and will be increased further.
      if (oldSize > limit && newSize > limit && newSize > oldSize) {
        return false;
      }

      const isPaste = transaction.getMeta("paste");

      // Block all exceeding transactions that were not pasted.
      if (!isPaste) {
        return false;
      }

      // For pasted content, we try to remove the exceeding content.
      const pos = transaction.selection.$head.pos;
      const over = newSize - limit;
      const from = pos - over;
      const to = pos;

      // It’s probably a bad idea to mutate transactions within `filterTransaction`
      // but for now this is working fine.
      transaction.deleteRange(from, to);

      // In some situations, the limit will continue to be exceeded after trimming.
      // This happens e.g. when truncating within a complex node (e.g. table)
      // and ProseMirror has to close this node again.
      // If this is the case, we prevent the transaction completely.
      const updatedSize = countCharacters(transaction.doc, options);

      if (updatedSize > limit) {
        return false;
      }

      return true;
    },
  });
}
